/**
 * PHARMAKON - Toast Provider
 * Manages toast notifications throughout the app
 *
 * Also bridges the module-level `pharmakon:toast` CustomEvent into the
 * React toast state — used by the axios interceptor in services/api.client.js
 * to flag 403 FORBIDDEN responses (e.g. module-not-activated) where there
 * is no React context to call directly.
 *
 * The context object itself lives in toastContext.js so this module exports
 * only its component (Fast Refresh).
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { ToastContext } from "./toastContext.js";
import { normalizeToastType, resolveToastLifetime } from "../lib/toast.js";

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /**
   * @param {React.ReactNode} message
   * @param {"success"|"info"|"warning"|"error"|"loading"} [type]
   * @param {number} [duration] ms; a floor-bounded request — the tone lifetime
   *   of ux/01 §3.9 is the minimum, errors and `loading` are persistent, `0`
   *   makes any toast persistent (lib/toast.js resolveToastLifetime).
   * @param {{ action?: { label: string, onClick: () => void } }} [opts]
   * @returns {string} the toast id (for removeToast)
   *
   * Lifetimes run in the single ToastHost (App level), which pauses them
   * while the toast is hovered or focused.
   */
  const addToast = useCallback((message, type = "info", duration, opts = {}) => {
    const id = Math.random().toString(36).slice(2);
    const toast = {
      id,
      message,
      type: normalizeToastType(type),
      duration: resolveToastLifetime(type, duration),
      action: opts.action,
    };
    setToasts((prev) => [...prev, toast]);
    return id;
  }, []);

  // Bridge: forward `window.dispatchEvent(new CustomEvent("pharmakon:toast"))`
  // from non-React modules (axios interceptors, plain helpers) into the
  // toast queue. Mounted once with the Provider so it's always available.
  useEffect(() => {
    const handler = (e) => {
      const { message, type = "info", duration } = e.detail ?? {};
      if (!message) return;
      addToast(message, type, duration);
    };
    window.addEventListener("pharmakon:toast", handler);
    return () => window.removeEventListener("pharmakon:toast", handler);
  }, [addToast]);

  const value = useMemo(
    () => ({
      toasts,
      addToast,
      removeToast,
      success: (msg, duration, opts) => addToast(msg, "success", duration, opts),
      info: (msg, duration, opts) => addToast(msg, "info", duration, opts),
      warning: (msg, duration, opts) => addToast(msg, "warning", duration, opts),
      error: (msg, duration, opts) => addToast(msg, "error", duration, opts),
      loading: (msg) => addToast(msg, "loading", 0),
    }),
    [toasts, addToast, removeToast],
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
};

export default ToastProvider;
